/**
 * APP-6D SIDC (Symbol Identification Code) options and utilities
 * 20-Character Number-Based Format
 *
 * Structure:
 * Position:  0-1  2    3    4-5  6    7     8-9   10-19
 * Field:     VER  CTX  STD  SS   ST   HTFD  EM    FID
 */

export interface SidcOption {
    value: string;
    label: string;
    group?: string | null;
    subgroup?: string | null;
}

export interface SidcOptionGroup {
    id: string;
    groupName: string;
    subgroups?: SidcOptionGroup[];
}

// Standard Identity (Position 3) - Affiliation
export const IDENTITY_OPTIONS: SidcOption[] = [
    { value: '0', label: 'Assumed Unknown' },
    { value: '1', label: 'Unknown' },
    { value: '2', label: 'Assumed Friend' },
    { value: '3', label: 'Friend' },
    { value: '4', label: 'Neutral' },
    { value: '5', label: 'Suspect/Joker' },
    { value: '6', label: 'Hostile/Faker' },
];

// Symbol Set (Positions 4-5) - Replaces Battle Dimension
export const SYMBOL_SET_OPTIONS: SidcOption[] = [
    { value: '10', label: 'Land Unit' },
    { value: '15', label: 'Land Equipment' },
    { value: '20', label: 'Land Installation' },
    { value: '30', label: 'Sea Surface' },
    { value: '35', label: 'Sea Subsurface' },
    { value: '50', label: 'Air' },
    { value: '51', label: 'Air Missile' },
];

// Status (Position 6)
export const STATUS_OPTIONS: SidcOption[] = [
    { value: '0', label: 'Present' },
    { value: '1', label: 'Planned/Anticipated' },
    { value: '2', label: 'Present - Fully Capable' },
    { value: '3', label: 'Present - Damaged' },
    { value: '4', label: 'Present - Destroyed' },
];

// HQ/Task Force/Feint/Dummy (Position 7)
export const HQTFD_OPTIONS: SidcOption[] = [
    { value: '0', label: 'None' },
    { value: '1', label: 'Feint/Dummy' },
    { value: '2', label: 'Headquarters' },
    { value: '3', label: 'Feint/Dummy HQ' },
    { value: '4', label: 'Task Force' },
    { value: '5', label: 'Feint/Dummy TF' },
    { value: '6', label: 'Task Force HQ' },
    { value: '7', label: 'Feint/Dummy TF HQ' },
];

// Echelon/Mobility (Positions 8-9)
export const ECHELON_OPTIONS: SidcOption[] = [
    { value: '00', label: 'Unspecified' },
    { value: '11', label: 'Team/Crew' },
    { value: '12', label: 'Squad' },
    { value: '13', label: 'Section' },
    { value: '14', label: 'Platoon/Detachment' },
    { value: '15', label: 'Company/Battery/Troop' },
    { value: '16', label: 'Battalion/Squadron' },
    { value: '17', label: 'Regiment/Group' },
    { value: '18', label: 'Brigade' },
    { value: '19', label: 'Division' },
    { value: '20', label: 'Corps/MEF' },
    { value: '21', label: 'Army' },
    { value: '22', label: 'Army Group/Front' },
    { value: '23', label: 'Region/Theater' },
    { value: '24', label: 'Command' },
];

export const UNIT_TYPE_GROUPS: SidcOptionGroup[] = [
    {
      "id": "cmd-ctrl",
      "groupName": "Command and Control",
      "subgroups": [
        { "id": "cmd-ctrl-liaison", "groupName": "Liaison" },
        { "id": "cmd-ctrl-miso", "groupName": "Military Information Support Operations (MISO)" },
        { "id": "cmd-ctrl-signal", "groupName": "Signal" }
      ]
    },
    {
      "id": "movement-maneuver",
      "groupName": "Movement and Maneuver",
      "subgroups": [
        { "id": "movement-maneuver-antitank", "groupName": "Antitank/Antiarmor" },
        { "id": "movement-maneuver-armor", "groupName": "Armor /Mechanized" },
        { "id": "movement-maneuver-army-aviation", "groupName": "Army Aviation/Aviation Rotary Wing" },
        { "id": "movement-maneuver-aviation-fixed", "groupName": "Aviation Fixed Wing" },
        { "id": "movement-maneuver-infantry", "groupName": "Infantry" },
        { "id": "movement-maneuver-recon", "groupName": "Reconnaissance/Cavalry/Scout" },
        { "id": "movement-maneuver-sof", "groupName": "Special Operations Forces (SOF)" }
      ]
    },
    {
      "id": "fires",
      "groupName": "Fires",
      "subgroups": [
        { "id": "fires-air-defense", "groupName": "Air Defense" },
        { "id": "fires-field-artillery", "groupName": "Field Artillery" },
        { "id": "fires-mortar", "groupName": "Mortar" }
      ]
    },
    {
      "id": "protection",
      "groupName": "Protection",
      "subgroups": [
        { "id": "protection-cbrn", "groupName": "Chemical Biological Radiological Nuclear Defense" },
        { "id": "protection-engineer", "groupName": "Engineer" },
        { "id": "protection-security", "groupName": "Security" },
        { "id": "protection-sar", "groupName": "Search and Rescue" }
      ]
    },
    {
      "id": "intelligence",
      "groupName": "Intelligence",
      "subgroups": [
        { "id": "intelligence-ew", "groupName": "Electronic Warfare" }
      ]
    },
    {
      "id": "sustainment",
      "groupName": "Sustainment",
      "subgroups": [
        { "id": "sustainment-band", "groupName": "Band" }
      ]
    },
    {
      "id": "naval",
      "groupName": "Naval",
      "subgroups": []
    },
    {
      "id": "named-hq",
      "groupName": "Named Headquarters",
      "subgroups": []
    },
    {
      "id": "law-enforcement",
      "groupName": "Law Enforcement",
      "subgroups": []
    }
  ];

// Unit Types - Function ID (Positions 10-15) for Land Units (Symbol Set 10)
export const UNIT_TYPE_OPTIONS: SidcOption[] = [
    { "value": "000000", "label": "Unspecified", "group": null, "subgroup": null },
    { "value": "110000", "label": "Command and Control", "group": "cmd-ctrl", "subgroup": null },
    { "value": "110100", "label": "Broadcast Transmitter Antennae", "group": "cmd-ctrl", "subgroup": null },
    { "value": "110200", "label": "Civil Affairs", "group": "cmd-ctrl", "subgroup": null },
    { "value": "110300", "label": "Civil–Military Cooperation", "group": "cmd-ctrl", "subgroup": null },
    { "value": "110400", "label": "Information Operations", "group": "cmd-ctrl", "subgroup": null },
    { "value": "110500", "label": "Liaison", "group": "cmd-ctrl", "subgroup": null },
    { "value": "110501", "label": "Reconnaissance and Liaison Element", "group": "cmd-ctrl", "subgroup": "cmd-ctrl-liaison" },
    { "value": "110600", "label": "Military Information Support Operations (MISO)", "group": "cmd-ctrl", "subgroup": null },
    { "value": "110601", "label": "Broadcast Transmitter Antennae", "group": "cmd-ctrl", "subgroup": "cmd-ctrl-miso" },
    { "value": "110700", "label": "Radio", "group": "cmd-ctrl", "subgroup": null },
    { "value": "110800", "label": "Radio Relay", "group": "cmd-ctrl", "subgroup": null },
    { "value": "110900", "label": "Radio Teletype Center", "group": "cmd-ctrl", "subgroup": null },
    { "value": "111000", "label": "Signal", "group": "cmd-ctrl", "subgroup": null },
    { "value": "111001", "label": "Radio", "group": "cmd-ctrl", "subgroup": "cmd-ctrl-signal" },
    { "value": "111002", "label": "Radio Relay", "group": "cmd-ctrl", "subgroup": "cmd-ctrl-signal" },
    { "value": "111003", "label": "Teletype", "group": "cmd-ctrl", "subgroup": "cmd-ctrl-signal" },
    { "value": "111005", "label": "Video Imagery (Combat Camera)", "group": "cmd-ctrl", "subgroup": "cmd-ctrl-signal" },
    { "value": "111200", "label": "Video Imagery (Combat Camera)", "group": "cmd-ctrl", "subgroup": null},
    { "value": "111300", "label": "Space", "group": "cmd-ctrl", "subgroup": null },
    { "value": "111400", "label": "Special Troops", "group": "cmd-ctrl", "subgroup": null },
    { "value": "111500", "label": "Multi-Domain Operations", "group": "cmd-ctrl", "subgroup": null },
    { "value": "120100", "label": "Air Assault with Organic Lift", "group": "movement-maneuver", "subgroup": null },
    { "value": "120200", "label": "Air Traffic Services/Airfield Operations", "group": "movement-maneuver", "subgroup": null },
    { "value": "120400", "label": "Antitank/Antiarmor", "group": "movement-maneuver", "subgroup": null},
    { "value": "120401", "label": "Armored", "group": "movement-maneuver", "subgroup": "movement-maneuver-antitank" },
    { "value": "120402", "label": "Motorized", "group": "movement-maneuver", "subgroup": "movement-maneuver-antitank" },
    { "value": "120500", "label": "Armor /Mechanized", "group": "movement-maneuver", "subgroup": null },
    { "value": "120501", "label": "Reconnaissance/Cavalry/Scout", "group": "movement-maneuver", "subgroup": "movement-maneuver-armor" },
    { "value": "120502", "label": "Amphibious", "group": "movement-maneuver", "subgroup": "movement-maneuver-armor" },
    { "value": "120600", "label": "Army Aviation/Aviation Rotary Wing", "group": "movement-maneuver", "subgroup": null },
    { "value": "120601", "label": "Reconnaissance", "group": "movement-maneuver", "subgroup": "movement-maneuver-army-aviation" },
    { "value": "120700", "label": "Aviation Composite", "group": "movement-maneuver", "subgroup": null},
    { "value": "120800", "label": "Aviation Fixed Wing", "group": "movement-maneuver", "subgroup": null},
    { "value": "120801", "label": "Reconnaissance", "group": "movement-maneuver", "subgroup": "movement-maneuver-aviation-fixed" },
    { "value": "120900", "label": "Combat", "group": "movement-maneuver", "subgroup": null },
    { "value": "121000", "label": "Combined Arms", "group": "movement-maneuver", "subgroup": null },
    { "value": "121100", "label": "Infantry", "group": "movement-maneuver", "subgroup": null },
    { "value": "121101", "label": "Amphibious", "group": "movement-maneuver", "subgroup": "movement-maneuver-infantry" },
    { "value": "121102", "label": "Armored/Mechanized/Tracked", "group": "movement-maneuver", "subgroup": "movement-maneuver-infantry" },
    { "value": "121103", "label": "Main Gun System", "group": "movement-maneuver", "subgroup": "movement-maneuver-infantry" },
    { "value": "121104", "label": "Motorized", "group": "movement-maneuver", "subgroup": "movement-maneuver-infantry" },
    { "value": "121105", "label": "Infantry Fighting Vehicle", "group": "movement-maneuver", "subgroup": "movement-maneuver-infantry" },
    { "value": "121106", "label": "Mobile Gun System", "group": "movement-maneuver", "subgroup": "movement-maneuver-infantry" },
    { "value": "121200", "label": "Observer", "group": "movement-maneuver", "subgroup": null },
    { "value": "121300", "label": "Reconnaissance/Cavalry/Scout", "group": "movement-maneuver", "subgroup": null },
    { "value": "121301", "label": "Reconnaissance and Surveillance", "group": "movement-maneuver", "subgroup": "movement-maneuver-recon" },
    { "value": "121302", "label": "Marine", "group": "movement-maneuver", "subgroup": "movement-maneuver-recon" },
    { "value": "121303", "label": "Motorized", "group": "movement-maneuver", "subgroup": "movement-maneuver-recon" },
    { "value": "121400", "label": "Sea Air Land (SEAL)", "group": "movement-maneuver", "subgroup": null},
    { "value": "121500", "label": "Sniper", "group": "movement-maneuver", "subgroup": null },
    { "value": "121600", "label": "Surveillance", "group": "movement-maneuver", "subgroup": null },
    { "value": "121700", "label": "Special Forces", "group": "movement-maneuver", "subgroup": null },
    { "value": "121800", "label": "Special Operations Forces (SOF)", "group": "movement-maneuver", "subgroup": null },
    { "value": "121801", "label": "Fixed Wing MISO", "group": "movement-maneuver", "subgroup": "movement-maneuver-sof" },
    { "value": "121802", "label": "Ground", "group": "movement-maneuver", "subgroup": "movement-maneuver-sof" },
    { "value": "121805", "label": "Underwater Demolition", "group": "movement-maneuver", "subgroup": "movement-maneuver-sof" },
    { "value": "121900", "label": "Unmanned Aerial Systems", "group": "movement-maneuver", "subgroup": null },
    { "value": "122000", "label": "Ranger", "group": "movement-maneuver", "subgroup": null },
    { "value": "130100", "label": "Air Defense", "group": "fires", "subgroup": null },
    { "value": "130101", "label": "Main Gun System", "group": "fires", "subgroup": "fires-air-defense"},
    { "value": "130102", "label": "Missile", "group": "fires", "subgroup": "fires-air-defense" },
    { "value": "130103", "label": "Air and Missile Defense", "group": "fires", "subgroup": "fires-air-defense" },
    { "value": "130200", "label": "Air/Land Naval Gunfire Liaison", "group": "fires", "subgroup": null},
    { "value": "130300", "label": "Field Artillery", "group": "fires", "subgroup": null },
    { "value": "130301", "label": "Self-propelled", "group": "fires", "subgroup": "fires-field-artillery" },
    { "value": "130400", "label": "Field Artillery Observer", "group": "fires", "subgroup": null },
    { "value": "130500", "label": "Joint Fire Support", "group": "fires", "subgroup": null },
    { "value": "130600", "label": "Meteorological", "group": "fires", "subgroup": null },
    { "value": "130700", "label": "Missile", "group": "fires", "subgroup": null },
    { "value": "130800", "label": "Mortar", "group": "fires", "subgroup": null },
    { "value": "130802", "label": "Self-Propelled Wheeled", "group": "fires"    , "subgroup": "fires-mortar"},
    { "value": "130900", "label": "Survey", "group": "fires", "subgroup": null },
    { "value": "140100", "label": "Chemical Biological Radiological Nuclear Defense", "group": "protection", "subgroup": null },
    { "value": "140102", "label": "Motorized", "group": "protection", "subgroup": "protection-cbrn" },
    { "value": "140103", "label": "Reconnaissance", "group": "protection", "subgroup": "protection-cbrn"},
    { "value": "140105", "label": "Reconnaissance Equipped", "group": "protection", "subgroup": "protection-cbrn" },
    { "value": "140106", "label": "Chemical, Biological, Radiological, Nuclear, and High-Yield Explosives", "group": "protection", "subgroup": "protection-cbrn" },
    { "value": "140200", "label": "Combat Support (Maneuver Enhancement)", "group": "protection", "subgroup": null },
    { "value": "140300", "label": "Criminal Investigation Division", "group": "protection", "subgroup": null },
    { "value": "140400", "label": "Diving", "group": "protection", "subgroup": null },
    { "value": "140500", "label": "Dog", "group": "protection", "subgroup": null },
    { "value": "140600", "label": "Drilling", "group": "protection", "subgroup": null },
    { "value": "140700", "label": "Engineer", "group": "protection", "subgroup": null },
    { "value": "140701", "label": "Mechanized", "group": "protection", "subgroup": "protection-engineer"},
    { "value": "140702", "label": "Motorized", "group": "protection", "subgroup": "protection-engineer"},
    { "value": "140703", "label": "Reconnaissance", "group": "protection", "subgroup": "protection-engineer" },
    { "value": "140800", "label": "Explosive Ordnance Disposal (EOD)", "group": "protection", "subgroup": null },
    { "value": "140900", "label": "Field Camp Construction", "group": "protection", "subgroup": null },
    { "value": "141000", "label": "Fire Fighting/Fire Protection", "group": "protection", "subgroup": null },
    { "value": "141100", "label": "Geospatial Support/Geospatial Information Support", "group": "protection", "subgroup": null },
    { "value": "141200", "label": "Military Police", "group": "protection", "subgroup": null },
    { "value": "141300", "label": "Mine", "group": "protection", "subgroup": null },
    { "value": "141400", "label": "Mine Clearing", "group": "protection", "subgroup": null },
    { "value": "141500", "label": "Mine Launching", "group": "protection", "subgroup": null },
    { "value": "141600", "label": "Mine Laying", "group": "protection", "subgroup": null },
    { "value": "141700", "label": "Security", "group": "protection", "subgroup": null },
    { "value": "141702", "label": "Motorized", "group": "protection", "subgroup": "protection-security"},
    { "value": "141800", "label": "Search and Rescue", "group": "protection", "subgroup": null },
    { "value": "141801", "label": "Isolated Personnel", "group": "protection", "subgroup": "protection-sar" },
    { "value": "142000", "label": "Shore Patrol/Security Police", "group": "protection", "subgroup": null },
    { "value": "142100", "label": "Topographic/Geospatial", "group": "protection", "subgroup": null },
    { "value": "142200", "label": "Missile Defense", "group": "protection", "subgroup": null },
    { "value": "150100", "label": "Analysis", "group": "intelligence", "subgroup": null },
    { "value": "150200", "label": "Counterintelligence", "group": "intelligence", "subgroup": null },
    { "value": "150300", "label": "Direction Finding", "group": "intelligence", "subgroup": null },
    { "value": "150400", "label": "Electronic Ranging", "group": "intelligence", "subgroup": null },
    { "value": "150500", "label": "Electronic Warfare", "group": "intelligence", "subgroup": null },
    { "value": "150501", "label": "Analysis", "group": "intelligence", "subgroup": "intelligence-ew" },
    { "value": "150502", "label": "Direction Finding", "group": "intelligence", "subgroup": "intelligence-ew" },
    { "value": "150503", "label": "Intercept", "group": "intelligence", "subgroup": "intelligence-ew" },
    { "value": "150504", "label": "Jamming", "group": "intelligence", "subgroup": "intelligence-ew" },
    { "value": "150505", "label": "Search", "group": "intelligence", "subgroup": "intelligence-ew" },
    { "value": "150600", "label": "Intercept (Search and Recording)", "group": "intelligence", "subgroup": null },
    { "value": "150700", "label": "Interrogation", "group": "intelligence", "subgroup": null },
    { "value": "150800", "label": "Jamming", "group": "intelligence", "subgroup": null },
    { "value": "150900", "label": "Joint Intelligence Center", "group": "intelligence", "subgroup": null},
    { "value": "151000", "label": "Military Intelligence", "group": "intelligence", "subgroup": null },
    { "value": "151100", "label": "Search", "group": "intelligence", "subgroup": null },
    { "value": "151200", "label": "Sensor", "group": "intelligence", "subgroup": null },
    { "value": "151300", "label": "Military History", "group": "intelligence", "subgroup": null },
    { "value": "160000", "label": "Sustainment", "group": "sustainment", "subgroup": null },
    { "value": "160100", "label": "Administrative", "group": "sustainment", "subgroup": null },
    { "value": "160200", "label": "All Classes of Supply", "group": "sustainment", "subgroup": null },
    { "value": "160400", "label": "Ammunition", "group": "sustainment", "subgroup": null },
    { "value": "160500", "label": "Band", "group": "sustainment", "subgroup": null },
    { "value": "160501", "label": "Army Music", "group": "sustainment", "subgroup": "sustainment-band"},
    { "value": "160600", "label": "Combat Service Support", "group": "sustainment", "subgroup": null },
    { "value": "160700", "label": "Finance", "group": "sustainment", "subgroup": null },
    { "value": "160800", "label": "Judge Advocate General", "group": "sustainment", "subgroup": null },
    { "value": "160900", "label": "Labor", "group": "sustainment", "subgroup": null },
    { "value": "161000", "label": "Laundry/Bath", "group": "sustainment", "subgroup": null },
    { "value": "161100", "label": "Maintenance", "group": "sustainment", "subgroup": null },
    { "value": "161200", "label": "Material", "group": "sustainment", "subgroup": null },
    { "value": "161300", "label": "Medical", "group": "sustainment", "subgroup": null },
    { "value": "161400", "label": "Medical Treatment Facility", "group": "sustainment", "subgroup": null},
    { "value": "161500", "label": "Morale, Welfare and Recreation", "group": "sustainment", "subgroup": null },
    { "value": "161600", "label": "Mortuary Affairs", "group": "sustainment", "subgroup": null },
    { "value": "161700", "label": "Multiple Classes of Supply", "group": "sustainment", "subgroup": null},
    { "value": "161800", "label": "NATO Supply Class I", "group": "sustainment", "subgroup": null },
    { "value": "161900", "label": "NATO Supply Class II", "group": "sustainment", "subgroup": null },
    { "value": "162000", "label": "NATO Supply Class III", "group": "sustainment", "subgroup": null },
    { "value": "162100", "label": "NATO Supply Class IV", "group": "sustainment", "subgroup": null },
    { "value": "162200", "label": "NATO Supply Class V", "group": "sustainment", "subgroup": null },
    { "value": "162300", "label": "Ordnance", "group": "sustainment", "subgroup": null },
    { "value": "162400", "label": "Personnel Services", "group": "sustainment", "subgroup": null },
    { "value": "162500", "label": "Petroleum, Oil and Lubricants", "group": "sustainment", "subgroup": null },
    { "value": "162800", "label": "Public Affairs/Public Information", "group": "sustainment", "subgroup": null },
    { "value": "162900", "label": "Quartermaster", "group": "sustainment", "subgroup": null },
    { "value": "163100", "label": "Religious Support", "group": "sustainment", "subgroup": null },
    { "value": "163200", "label": "Replacement Holding Unit", "group": "sustainment", "subgroup": null},
    { "value": "163500", "label": "Joint Information Bureau (JIB)", "group": "sustainment", "subgroup": null },
    { "value": "163600", "label": "Transportation", "group": "sustainment", "subgroup": null },
    { "value": "163700", "label": "US Supply Class I", "group": "sustainment", "subgroup": null },
    { "value": "164000", "label": "US Supply Class IV", "group": "sustainment", "subgroup": null },
    { "value": "164200", "label": "US Supply Class VI", "group": "sustainment", "subgroup": null },
    { "value": "164300", "label": "US Supply Class VII", "group": "sustainment", "subgroup": null },
    { "value": "164400", "label": "US Supply Class VIII", "group": "sustainment", "subgroup": null },
    { "value": "164500", "label": "US Supply Class IX", "group": "sustainment", "subgroup": null },
    { "value": "164600", "label": "US Supply Class X", "group": "sustainment", "subgroup": null },
    { "value": "164800", "label": "Water Purification", "group": "sustainment", "subgroup": null },
    { "value": "164900", "label": "Broadcast", "group": "sustainment", "subgroup": null },
    { "value": "165100", "label": "Interpreter/Translator", "group": "sustainment", "subgroup": null },
    { "value": "165200", "label": "Support", "group": "sustainment", "subgroup": null },
    { "value": "165300", "label": "Army Field Support", "group": "sustainment", "subgroup": null },
    { "value": "165400", "label": "Contracting Services", "group": "sustainment", "subgroup": null },
    { "value": "165500", "label": "Parachute Rigger", "group": "sustainment", "subgroup": null },
    { "value": "165600", "label": "Human Resources", "group": "sustainment", "subgroup": null },
    { "value": "170100", "label": "Naval", "group": "naval", "subgroup": null },
    { "value": "180100", "label": "Allied Command Europe Rapid Reaction Corps (ARRC)", "group": "named-hq", "subgroup": null },
    { "value": "180200", "label": "Allied Command Operations", "group": "named-hq", "subgroup": null },
    { "value": "180300", "label": "International Security Assistance Force (ISAF)", "group": "named-hq", "subgroup": null },
    { "value": "180400", "label": "Multinational (MN)", "group": "named-hq", "subgroup": null },
    { "value": "190000", "label": "Emergency Operation", "group": "emergency", "subgroup": null },
    { "value": "200000", "label": "Law Enforcement", "group": "law-enforcement", "subgroup": null },
    { "value": "200100", "label": "Bureau of Alcohol, Tobacco, Firearms and Explosives (ATF) (Departmentof Justice)", "group": "law-enforcement", "subgroup": null },
    { "value": "200200", "label": "Border Patrol", "group": "law-enforcement", "subgroup": null },
    { "value": "200300", "label": "Customs Service", "group": "law-enforcement", "subgroup": null },
    { "value": "200400", "label": "Drug Enforcement Administration (DEA)", "group": "law-enforcement", "subgroup": null },
    { "value": "200500", "label": "Department of Justice (DOJ)", "group": "law-enforcement", "subgroup": null },
    { "value": "200600", "label": "Federal Bureau of Investigation (FBI)", "group": "law-enforcement", "subgroup": null },
    { "value": "200700", "label": "Police", "group": "law-enforcement", "subgroup": null },
    { "value": "200800", "label": "Prison", "group": "law-enforcement", "subgroup": null },
    { "value": "200900", "label": "United States Secret Service (Treas) (USSS)", "group": "law-enforcement", "subgroup": null },
    { "value": "201000", "label": "Transportation Security Administration (TSA)", "group": "law-enforcement", "subgroup": null },
    { "value": "201100", "label": "Coast Guard", "group": "law-enforcement", "subgroup": null },
    { "value": "201200", "label": "US Marshals Service", "group": "law-enforcement", "subgroup": null },
    { "value": "201300", "label": "Internal Security Force", "group": "law-enforcement", "subgroup": null },
    { "value": "210000", "label": "Cyber", "group": "cyber", "subgroup": null }
  ]
;

export interface SidcParts {
    identity: string;
    symbolSet: string;
    status: string;
    hqtfd: string;
    echelon: string;
    unitType: string;
}

/**
 * Build 20-character APP-6D SIDC from selections
 */
export function buildSidc(
    identity: string,
    symbolSet: string,
    status: string,
    hqtfd: string,
    echelon: string,
    unitType: string
): string {
    const version = '10';      // 2525D/APP-6D
    const context = '0';       // Reality
    const paddedUnitType = unitType.padEnd(10, '0');

    return `${version}${context}${identity}${symbolSet}${status}${hqtfd}${echelon}${paddedUnitType}`;
}

/**
 * Parse existing 20-char SIDC into component parts
 */
export function parseSidc(sidc: string): SidcParts {
    // Default values for invalid or missing SIDC
    if (!sidc || sidc.length < 20) {
        return {
            identity: '3',
            symbolSet: '10',
            status: '0',
            hqtfd: '0',
            echelon: '15',
            unitType: '1211000000',
        };
    }
    return {
        identity: sidc[3],
        symbolSet: sidc.substring(4, 6),
        status: sidc[6],
        hqtfd: sidc[7],
        echelon: sidc.substring(8, 10),
        unitType: sidc.substring(10, 20),
    };
}

/**
 * Validate if a value exists in the given options array
 */
export function isValidOption(value: string, options: SidcOption[]): boolean {
    return options.some(opt => opt.value === value);
}

/**
 * Get label for a given value from options array
 */
export function getOptionLabel(value: string, options: SidcOption[]): string | undefined {
    return options.find(opt => opt.value === value)?.label;
}

/**
 * Get the subordinate echelon value (one level lower in the hierarchy)
 * Used when creating child units to auto-select an appropriate echelon
 *
 * @param parentEchelon - Parent unit's echelon value (e.g., '15' for Company)
 * @returns The next lower echelon value, or Team/Crew if already at minimum
 */
export function getSubordinateEchelon(parentEchelon: string): string {
    const currentIndex = ECHELON_OPTIONS.findIndex(opt => opt.value === parentEchelon);

    // If not found, unspecified, or already at Team/Crew (lowest), return Team/Crew
    if (currentIndex <= 1) {
        return '11'; // Team/Crew
    }

    // Return one level lower (previous index in array)
    return ECHELON_OPTIONS[currentIndex - 1].value;
}

/**
 * Get echelon options filtered to only include sizes smaller than the parent
 * Used in ORBAT creator to enforce hierarchical constraints
 *
 * @param parentEchelon - Parent unit's echelon value (e.g., '16' for Battalion)
 * @returns Array of echelon options smaller than the parent, or all options if parent is not found
 */
export function getSubordinateEchelonOptions(parentEchelon: string): SidcOption[] {
    const parentIndex = ECHELON_OPTIONS.findIndex(opt => opt.value === parentEchelon);

    // If parent not found or is unspecified, return all options
    if (parentIndex <= 0) {
        return ECHELON_OPTIONS;
    }

    // Return only echelons smaller than parent (lower indices in array)
    // Include 'Unspecified' (index 0) as well for flexibility
    return ECHELON_OPTIONS.slice(0, parentIndex);
}

/**
 * Limited status options for ORBAT creator context
 * Only includes "Present" and "Planned/Anticipated"
 */
export const LIMITED_STATUS_OPTIONS: SidcOption[] = [
    { value: '0', label: 'Present' },
    { value: '1', label: 'Planned/Anticipated' },
];

/**
 * Simplified HQTFD options for ORBAT creator (only None and Dummy)
 */
export const SIMPLIFIED_HQTFD_OPTIONS: SidcOption[] = [
    { value: '0', label: 'Normal Unit' },
    { value: '1', label: 'Dummy Unit' },
];

/**
 * Special "Unspecified" group option for unit types without a group
 */
export const UNSPECIFIED_GROUP: SidcOptionGroup = {
    id: 'unspecified',
    groupName: 'Unspecified',
    subgroups: [],
};

/**
 * Get all unique groups from UNIT_TYPE_OPTIONS
 * Returns groups in order they appear in UNIT_TYPE_GROUPS, plus "Unspecified" for null groups
 */
export function getUnitTypeGroups(): SidcOptionGroup[] {
    const groups: SidcOptionGroup[] = [UNSPECIFIED_GROUP];

    // Add all defined groups from UNIT_TYPE_GROUPS
    UNIT_TYPE_GROUPS.forEach(group => {
        groups.push(group);
    });

    return groups;
}

/**
 * Get subgroups for a given group ID
 * Returns empty array if group has no subgroups or doesn't exist
 */
export function getSubgroupsForGroup(groupId: string | null): SidcOptionGroup[] {
    if (groupId === null || groupId === 'unspecified') {
        return [];
    }

    const group = UNIT_TYPE_GROUPS.find(g => g.id === groupId);
    return group?.subgroups || [];
}

/**
 * Get unit type options filtered by group and optionally by subgroup
 *
 * @param groupId - The group ID to filter by (null for "Unspecified")
 * @param subgroupId - The subgroup ID to filter by (null for direct group members only)
 * @returns Filtered array of unit type options
 */
export function getUnitTypesForSelection(
    groupId: string | null,
    subgroupId: string | null
): SidcOption[] {
    // Handle "Unspecified" group - returns only units with null group
    if (groupId === null || groupId === 'unspecified') {
        return UNIT_TYPE_OPTIONS.filter(opt => opt.group === null);
    }

    // If a subgroup is selected, return only units matching that subgroup
    if (subgroupId !== null && subgroupId !== '') {
        return UNIT_TYPE_OPTIONS.filter(
            opt => opt.group === groupId && opt.subgroup === subgroupId
        );
    }

    // If no subgroup is selected, return units that belong to the group
    // but don't have a subgroup (direct group members)
    return UNIT_TYPE_OPTIONS.filter(
        opt => opt.group === groupId && (opt.subgroup === null || opt.subgroup === undefined)
    );
}

/**
 * Find the group and subgroup for a given unit type value
 * Used to initialize dropdowns when editing an existing unit
 *
 * @param unitTypeValue - The 6-character unit type value (e.g., "121100")
 * @returns Object containing groupId and subgroupId (can be null)
 */
export function findGroupAndSubgroupForUnitType(
    unitTypeValue: string
): { groupId: string | null; subgroupId: string | null } {
    // Remove trailing zeros for matching (unit type values are padded)
    const trimmedValue = unitTypeValue.replace(/0+$/, '').padEnd(6, '0').substring(0, 6);

    const unitType = UNIT_TYPE_OPTIONS.find(opt => opt.value === trimmedValue);

    if (!unitType) {
        return { groupId: null, subgroupId: null };
    }

    return {
        groupId: unitType.group || null,
        subgroupId: unitType.subgroup || null,
    };
}

